import { captureMonitorMessage } from '@/utils/monitor/core/client'
import { isMonitorReportable } from '@/utils/monitor/core/filter'
import { MONITOR_EVENT, MONITOR_PATH, MONITOR_SOURCE, getMonitorDescriptor, getMonitorErrorExtra } from '@/utils/monitor/schema'

// 挂在 axios config 上的内部监控字段，用来保存请求开始时间。
const REQUEST_MONITOR_KEY = '__monitorRequest'
const REQUEST_MONITOR_PATH = MONITOR_PATH.REQUEST_INFRA
// 超过这个耗时阈值的成功请求会按慢请求 warning 上报。
const SLOW_REQUEST_THRESHOLD_MS = 8000
// 慢请求 warning 的冷却时间，避免同一接口持续刷屏。
const REQUEST_WARNING_COOLDOWN_MS = 60000
// 请求异常的冷却时间，避免网络抖动或 5xx 连续触发大量重复告警。
const REQUEST_ERROR_COOLDOWN_MS = 15000

// 统一格式化请求方法，保证监控字段稳定。
const getRequestMethod = config => String(config?.method ?? 'get').toUpperCase()

// 统一读取请求地址，避免各处自己从 config 里取。
const getRequestUrl = config => config?.url ?? ''

// 计算单个请求耗时。开始时间由 request interceptor 写入。
const getRequestDuration = (config) => {
  const startedAt = config?.[REQUEST_MONITOR_KEY]?.startedAt
  if (!startedAt) {
    return undefined
  }

  return Date.now() - startedAt
}

// 统一拼装 request 事件的 extra 字段。
const getRequestBaseExtra = (config, extra = {}) => {
  return {
    request_url: getRequestUrl(config),
    request_method: getRequestMethod(config),
    request_base_url: config?.baseURL ?? '',
    duration_ms: getRequestDuration(config),
    ...extra,
  }
}

// 在请求发出时挂一个开始时间，供后续慢请求和失败上报复用。
export const attachRequestMonitor = (config = {}) => {
  config[REQUEST_MONITOR_KEY] = {
    startedAt: Date.now(),
  }

  return config
}

// 请求成功后只做一件事：检测是否为慢请求 warning。
export const reportRequestSuccess = (response) => {
  const config = response?.config
  const durationMs = getRequestDuration(config)

  if (!durationMs || durationMs < SLOW_REQUEST_THRESHOLD_MS) {
    return
  }

  const method = getRequestMethod(config)
  const url = getRequestUrl(config)
  const monitor = getMonitorDescriptor({
    monitorSource: MONITOR_SOURCE.REQUEST,
    monitorEvent: MONITOR_EVENT.SLOW_REQUEST,
    monitorPath: REQUEST_MONITOR_PATH,
  })

  captureMonitorMessage(monitor.title, {
    level: 'warning',
    dedupeKey: `request:slow:${method}:${url}`,
    cooldownMs: REQUEST_WARNING_COOLDOWN_MS,
    fingerprint: [monitor.title, method, url],
    tags: monitor.tags,
    extra: getRequestBaseExtra(config, {
      response_status: response?.status ?? 200,
    }),
  })
}

// 请求失败时只上报真正的系统问题：
// - 网络错误
// - HTTP 5xx
// 4xx 和被显式标记为不可上报的业务错误不会进监控。
export const reportRequestFailure = (error) => {
  if (!isMonitorReportable(error)) {
    return
  }

  const response = error?.response
  const status = response?.status
  const config = error?.config ?? response?.config

  if (status && status < 500) {
    return
  }

  const method = getRequestMethod(config)
  const url = getRequestUrl(config)
  const requestKind = status ? 'http' : 'network'
  const monitorEvent = requestKind === 'http' ? MONITOR_EVENT.HTTP_ERROR : MONITOR_EVENT.NETWORK_ERROR
  const monitor = getMonitorDescriptor({
    monitorSource: MONITOR_SOURCE.REQUEST,
    monitorEvent,
    monitorPath: REQUEST_MONITOR_PATH,
  })

  captureMonitorMessage(monitor.title, {
    payload: error,
    level: 'error',
    dedupeKey: `request:${requestKind}:${method}:${url}:${status ?? 'unknown'}`,
    cooldownMs: REQUEST_ERROR_COOLDOWN_MS,
    fingerprint: [monitor.title, method, url, String(status ?? 'unknown')],
    tags: monitor.tags,
    extra: getMonitorErrorExtra(error, getRequestBaseExtra(config, {
      response_status: status,
    })),
  })
}
